import { useEffect, useRef, useState } from 'react';

/**
 * Shrinks font size so text fits in its container on a single line — no wrapping, no ellipsis.
 * The returned ref must be placed on the element that holds the text.
 * That element must have `white-space: nowrap` and `overflow: hidden`.
 */
export function useFitText(
  text: string,
  maxFontSizePx: number,
  minFontSizePx = 10,
) {
  const ref = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSizePx);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      el.style.fontSize = `${maxFontSizePx}px`;
      let size = maxFontSizePx;
      while (el.scrollWidth > el.clientWidth && size > minFontSizePx) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
      setFontSize(size);
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, maxFontSizePx, minFontSizePx]);

  return { ref, fontSize };
}
